<template>
  <div class="bg-white p-6 rounded-lg shadow-md">
    <h2 class="text-2xl font-bold text-gray-800 mb-6">Manage Advertisements</h2>

    <!-- Tabs -->
    <div class="flex border-b mb-6">
      <button 
        @click="currentTab = 'pending'"
        :class="['px-6 py-2 font-semibold transition', currentTab === 'pending' ? 'text-[#BF3131] border-b-2 border-[#BF3131]' : 'text-gray-500 hover:text-gray-700']"
      >
        Pending Approval
      </button>
      <button 
        @click="currentTab = 'all'"
        :class="['px-6 py-2 font-semibold transition', currentTab === 'all' ? 'text-[#BF3131] border-b-2 border-[#BF3131]' : 'text-gray-500 hover:text-gray-700']"
      >
        All Advertisements
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-8 text-gray-500">
      Loading advertisements...
    </div>

    <!-- Empty State -->
    <div v-else-if="ads.length === 0" class="text-center py-8 bg-gray-50 rounded-lg text-gray-500">
      No advertisements found.
    </div>

    <!-- Ad List -->
    <div v-else class="grid grid-cols-1 gap-6">
      <div 
        v-for="ad in filteredAds" 
        :key="ad.id" 
        class="flex flex-col md:flex-row gap-4 p-4 border rounded-lg hover:shadow-md transition bg-white"
      >
        <!-- Image -->
        <div class="w-full md:w-32 h-32 flex-shrink-0 bg-gray-100 rounded-md overflow-hidden">
          <img 
            v-if="ad.image" 
            :src="resolveImage(ad.image)" 
            class="w-full h-full object-cover"
          />
          <div v-else class="w-full h-full flex items-center justify-center text-gray-400 text-xs">No Image</div>
        </div>

        <!-- Content -->
        <div class="flex-1">
          <div class="flex justify-between items-start">
            <h3 class="font-bold text-lg text-gray-800">{{ ad.title }}</h3>
            <span 
              :class="['px-2 py-1 text-xs rounded-full font-semibold', ad.is_approved ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700']"
            >
              {{ ad.is_approved ? 'Approved' : 'Pending' }}
            </span>
          </div>
          <p class="text-gray-600 text-sm mt-1 line-clamp-2">{{ ad.description }}</p>
          <div class="text-xs text-gray-400 mt-2">
            Publish Date: {{ formatDate(ad.publish_date) }} | User ID: {{ ad.user_id }}
          </div>
        </div>

        <!-- Actions -->
        <div class="flex md:flex-col gap-2 justify-center border-t md:border-t-0 md:border-l pt-4 md:pt-0 md:pl-4 mt-4 md:mt-0">
          <button 
            v-if="!ad.is_approved"
            @click="approveAd(ad.id)" 
            class="px-4 py-2 bg-green-500 text-white text-sm font-semibold rounded hover:bg-green-600 transition"
          >
            Approve
          </button>
          <button 
            @click="deleteAd(ad.id)" 
            class="px-4 py-2 bg-red-50 text-red-600 border border-red-200 text-sm font-semibold rounded hover:bg-red-100 transition"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';

export default {
  name: 'AdminAds',
  data() {
    return {
      currentTab: 'pending',
      ads: [],
      loading: true
    };
  },
  computed: {
    filteredAds() {
      if (this.currentTab === 'pending') {
        return this.ads.filter(ad => !ad.is_approved);
      }
      return this.ads;
    }
  },
  mounted() {
    this.fetchAds();
  },
  methods: {
    getBaseUrl() {
      return process.env.VUE_APP_API_BASE_URL || 'https://sanskritirdapoon.in/api';
    },
    async fetchAds() {
      this.loading = true;
      try {
        const res = await axios.get(`${this.getBaseUrl()}/admin/advertisments`);
        this.ads = res.data;
      } catch (err) {
        console.error("Error fetching ads:", err);
      } finally {
        this.loading = false;
      }
    },
    async approveAd(id) {
      const result = await Swal.fire({
        title: 'Approve Advertisement?',
        text: "Are you sure you want to approve this ad?",
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#10B981',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, approve!'
      });

      if (!result.isConfirmed) return;

      try {
        await axios.put(`${this.getBaseUrl()}/admin/advertisments/${id}/approve`);
        // Update local state
        const ad = this.ads.find(a => a.id === id);
        if (ad) ad.is_approved = 1;
        
        Swal.fire({
          icon: 'success',
          title: 'Approved!',
          text: 'Approved successfully!',
          confirmButtonColor: '#BF3131'
        });
      } catch (err) {
        console.error("Error approving ad:", err);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to approve.',
          confirmButtonColor: '#BF3131'
        });
      }
    },
     async deleteAd(id) {
      const result = await Swal.fire({
        title: 'Delete Advertisement?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!'
      });

      if (!result.isConfirmed) return;

      try {
        await axios.delete(`${this.getBaseUrl()}/admin/advertisments/${id}`);
        this.ads = this.ads.filter(a => a.id !== id);
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Advertisement deleted successfully.',
          confirmButtonColor: '#BF3131'
        });
      } catch (err) {
        console.error("Error deleting ad:", err);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to delete.',
          confirmButtonColor: '#BF3131'
        });
      }
    },
    resolveImage(img) {
        if (!img) return '';
        if (img.startsWith('data:') || img.startsWith('http')) return img;
        // Use FILE_BASE_URL env or fallback
        const fileBase = process.env.VUE_APP_FILE_BASE_URL || 'https://sanskritirdapoon.in';
        return `${fileBase}${img}`;
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString();
    }
  }
};
</script>
